import type { MobileGesture } from "../domain/app-types.ts";
import { horizontalGesture } from "./gesture.ts";

export interface MobileControllerActions {
  taskPanelOpen(): boolean;
  quickNotesOpen(): boolean;
  referenceOpen(): boolean;
  closeTask(skipAnimation?: boolean): void | Promise<unknown>;
  closeQuickNotes(): void | Promise<unknown>;
  closeReference(skipAnimation?: boolean): void | Promise<unknown>;
  toast(message: string): void;
}

export interface MobileController {
  bindGestures(): void;
  bindViewportResilience(): void;
  enableBackGuard(): void;
  openSidebar(): void;
  closeSidebar(restoreTrigger?: boolean): void;
  rememberSidebar(): void;
  restoreSidebar(sidebar: HTMLElement, preferredScroll: number): void;
  hapticFeedback(): void;
  hasSelectedText(): boolean;
  gestureStartsOnControl(target: Element): boolean;
}

export function createMobileController(actions: MobileControllerActions): MobileController {
  let sidebarScroll = 0;
  let sidebarFocusKey = "";
  let backGuardEnabled = false;
  let backExitDeadline = 0;
  let allowBackExit = false;
  let viewportBound = false;

  const focusKey = (element: HTMLElement | null): string => {
    if (!element) return "";
    if (element.dataset.view) return `view:${element.dataset.view}`;
    if (element.dataset.category) return `category:${element.dataset.category}`;
    if (element.dataset.root) return `root:${element.dataset.root}`;
    if (element.dataset.savedView) return `saved:${element.dataset.savedView}`;
    return "";
  };

  const focusTarget = (key: string): HTMLElement | null => {
    const [kind, id] = key.split(":", 2);
    if (!id) return null;
    const attribute = kind === "view" ? "data-view" : kind === "category" ? "data-category" : kind === "root" ? "data-root" : kind === "saved" ? "data-saved-view" : "";
    return attribute ? document.querySelector<HTMLElement>(`[${attribute}="${CSS.escape(id)}"]`) : null;
  };

  const rememberSidebar = (): void => {
    const sidebar = document.querySelector<HTMLElement>("#sidebar");
    if (!sidebar) return;
    sidebarScroll = sidebar.scrollTop;
    const active = document.activeElement instanceof HTMLElement && sidebar.contains(document.activeElement)
      ? document.activeElement.closest<HTMLElement>("[data-view],[data-category],[data-root],[data-saved-view]")
      : null;
    sidebarFocusKey = focusKey(active) || sidebarFocusKey;
  };

  const setSidebarModal = (open: boolean): void => {
    const sidebar = document.querySelector<HTMLElement>("#sidebar");
    const workspace = document.querySelector<HTMLElement>(".workspace");
    if (open) {
      sidebar?.setAttribute("role", "dialog");
      sidebar?.setAttribute("aria-modal", "true");
      sidebar?.setAttribute("aria-label", "Navigation");
      workspace?.setAttribute("inert", "");
    } else {
      sidebar?.removeAttribute("role");
      sidebar?.removeAttribute("aria-modal");
      sidebar?.removeAttribute("aria-label");
      workspace?.removeAttribute("inert");
    }
  };

  const openSidebar = (): void => {
    document.body.classList.add("nav-open");
    setSidebarModal(true);
    requestAnimationFrame(() => {
      const sidebar = document.querySelector<HTMLElement>("#sidebar");
      if (sidebar) sidebar.scrollTop = sidebarScroll;
      (focusTarget(sidebarFocusKey) || sidebar?.querySelector<HTMLElement>(".child-space.is-active,.space-button.is-active,.nav-button.is-active") || sidebar?.querySelector<HTMLElement>(".sidebar-close"))?.focus({ preventScroll: true });
    });
  };

  const closeSidebar = (restoreTrigger = true): void => {
    rememberSidebar();
    document.body.classList.remove("nav-open");
    setSidebarModal(false);
    if (restoreTrigger) document.querySelector<HTMLElement>("[data-action=open-sidebar]")?.focus({ preventScroll: true });
  };

  const gestureStartsOnControl = (target: Element): boolean => !!target.closest("input, textarea, select, button, a, label, summary, [contenteditable=true], [data-gesture-ignore], .drag-handle, .reorder-controls");
  const hasSelectedText = (): boolean => {
    const selection = window.getSelection();
    return !!selection && !selection.isCollapsed && !!selection.toString();
  };
  const hasHorizontalScroller = (target: Element): boolean => {
    let element: Element | null = target;
    while (element && element !== document.body) {
      if (element instanceof HTMLElement && element.scrollWidth > element.clientWidth) {
        const overflow = getComputedStyle(element).overflowX;
        if (overflow === "auto" || overflow === "scroll") return true;
      }
      element = element.parentElement;
    }
    return false;
  };
  const hapticFeedback = (): void => {
    if (typeof navigator.vibrate === "function") navigator.vibrate(8);
  };

  const bindViewportResilience = (): void => {
    if (viewportBound) return;
    viewportBound = true;
    const viewport = window.visualViewport;
    let expandedHeight = window.innerHeight;
    let layoutWidth = window.innerWidth;
    const update = () => {
      const mobile = window.matchMedia("(max-width: 820px)").matches;
      const visibleHeight = viewport?.height ?? window.innerHeight;
      if (window.innerWidth !== layoutWidth || !mobile) {
        layoutWidth = window.innerWidth;
        expandedHeight = window.innerHeight;
      }
      expandedHeight = Math.max(expandedHeight, window.innerHeight, visibleHeight);
      const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const textInput = focused?.matches('textarea:not([readonly]):not([disabled]), input:not([readonly]):not([disabled])')
        && !focused.matches('input[type="checkbox"], input[type="radio"], input[type="button"], input[type="submit"], input[type="reset"], input[type="file"], input[type="range"], input[type="color"], input[type="hidden"], input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"], input[type="week"]');
      const editing = Boolean(textInput || focused?.closest('[contenteditable="true"], [contenteditable=""]'));
      const keyboardOpen = mobile && editing && (!viewport || (viewport.scale === 1 && expandedHeight - visibleHeight > Math.max(120, expandedHeight * .18)));
      document.body.classList.toggle("mobile-keyboard-open", keyboardOpen);
      document.documentElement.style.setProperty("--mobile-viewport-height", mobile ? `${visibleHeight}px` : "100dvh");
      document.documentElement.style.setProperty("--mobile-viewport-top", mobile ? `${viewport?.offsetTop ?? 0}px` : "0px");
      if (keyboardOpen && focused?.closest(".detail-panel form, .collection-reference-panel, .quick-notes-panel")) requestAnimationFrame(() => {
        if (focused.isConnected && document.activeElement === focused) focused.scrollIntoView({ block: focused.closest(".quick-notes-panel") ? "nearest" : "center", inline: "nearest" });
      });
    };
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", () => requestAnimationFrame(update));
    update();
  };

  const bindGestures = (): void => {
    const mobileViewport = window.matchMedia("(max-width: 820px)");
    const settleMs = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 180;
    let gesture: { kind: MobileGesture; identifier: number; startX: number; startY: number; startedAt: number } | null = null;
    const gestureElement = (kind: MobileGesture) => kind === "close-task" ? document.querySelector<HTMLElement>(".detail-panel") : kind === "close-reference" ? document.querySelector<HTMLElement>(".collection-reference-panel") : document.querySelector<HTMLElement>(".sidebar");
    const clearStyles = (element: HTMLElement | null) => { element?.classList.remove("is-gesture-dragging", "is-gesture-settling"); element?.style.removeProperty("transform"); };
    const settle = (element: HTMLElement, transform: string, complete: () => void | Promise<unknown>) => {
      element.classList.remove("is-gesture-dragging");
      element.classList.add("is-gesture-settling");
      element.style.transform = transform;
      window.setTimeout(() => { void Promise.resolve(complete()).finally(() => clearStyles(element)); }, settleMs);
    };
    document.addEventListener("touchstart", event => {
      gesture = null;
      if (!mobileViewport.matches || event.touches.length !== 1 || hasSelectedText()) return;
      const touch = event.touches[0];
      const target = event.target instanceof Element ? event.target : null;
      if (!target || gestureStartsOnControl(target) || hasHorizontalScroller(target)) return;
      let kind: MobileGesture | null = null;
      if (actions.referenceOpen() && target.closest(".collection-reference-panel")) kind = "close-reference";
      else if (actions.taskPanelOpen() && target.closest(".detail-panel")) kind = "close-task";
      else if (document.body.classList.contains("nav-open") && target.closest(".sidebar")) kind = "close-sidebar";
      else if (!document.body.classList.contains("nav-open") && touch.clientX >= 24 && touch.clientX <= 96) kind = "open-sidebar";
      if (kind) gesture = { kind, identifier: touch.identifier, startX: touch.clientX, startY: touch.clientY, startedAt: performance.now() };
    }, { passive: true });
    document.addEventListener("touchmove", event => {
      if (!gesture) return;
      const touch = Array.from(event.touches).find(item => item.identifier === gesture?.identifier);
      if (!touch) { gesture = null; return; }
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      const element = gestureElement(gesture.kind);
      if (!element) { gesture = null; return; }
      const direction = gesture.kind === "close-sidebar" ? -1 : 1;
      const movement = horizontalGesture(deltaX, deltaY, element.offsetWidth, direction);
      if (!movement.distance || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      event.preventDefault();
      element.classList.add("is-gesture-dragging");
      const position = gesture.kind === "open-sidebar" ? -element.offsetWidth + movement.distance : gesture.kind === "close-sidebar" ? -movement.distance : movement.distance;
      element.style.transform = `translate3d(${position}px,0,0)`;
    }, { passive: false });
    document.addEventListener("touchend", event => {
      if (!gesture) return;
      const completed = gesture;
      gesture = null;
      const touch = Array.from(event.changedTouches).find(item => item.identifier === completed.identifier);
      if (!touch) return;
      const deltaX = touch.clientX - completed.startX;
      const deltaY = touch.clientY - completed.startY;
      const element = gestureElement(completed.kind);
      if (!element) return;
      const direction = completed.kind === "close-sidebar" ? -1 : 1;
      const velocity = Math.abs(deltaX) / Math.max(1, performance.now() - completed.startedAt);
      const movement = horizontalGesture(deltaX, deltaY, element.offsetWidth, direction, .28, velocity);
      if (!movement.committed) {
        settle(element, completed.kind === "open-sidebar" ? `translate3d(${-element.offsetWidth}px,0,0)` : "translate3d(0,0,0)", () => undefined);
        return;
      }
      hapticFeedback();
      if (completed.kind === "open-sidebar") settle(element, "translate3d(0,0,0)", openSidebar);
      if (completed.kind === "close-sidebar") settle(element, `translate3d(${-element.offsetWidth}px,0,0)`, closeSidebar);
      if (completed.kind === "close-task") settle(element, `translate3d(${element.offsetWidth}px,0,0)`, () => actions.closeTask(true));
      if (completed.kind === "close-reference") settle(element, `translate3d(${element.offsetWidth}px,0,0)`, () => actions.closeReference(true));
    }, { passive: true });
    document.addEventListener("touchcancel", () => { if (gesture) clearStyles(gestureElement(gesture.kind)); gesture = null; }, { passive: true });
  };

  const pushBackGuard = (): void => { history.pushState({ ...history.state, weirdoBackGuard: true }, ""); };
  const handleBack = (): void => {
    if (allowBackExit) return;
    const dialog = Array.from(document.querySelectorAll<HTMLDialogElement>("dialog[open]")).at(-1);
    if (dialog) {
      pushBackGuard(); backExitDeadline = 0;
      const cancelEvent = new Event("cancel", { cancelable: true });
      if (dialog.dispatchEvent(cancelEvent)) dialog.close();
      return;
    }
    if (actions.quickNotesOpen()) { pushBackGuard(); backExitDeadline = 0; void actions.closeQuickNotes(); return; }
    if (actions.referenceOpen()) { pushBackGuard(); backExitDeadline = 0; void actions.closeReference(); return; }
    if (actions.taskPanelOpen()) { pushBackGuard(); backExitDeadline = 0; void actions.closeTask(); return; }
    if (document.body.classList.contains("nav-open")) { pushBackGuard(); backExitDeadline = 0; closeSidebar(); return; }
    const now = Date.now();
    if (now <= backExitDeadline) { allowBackExit = true; history.back(); return; }
    backExitDeadline = now + 2000;
    pushBackGuard();
    actions.toast("Swipe back again to exit");
  };
  const enableBackGuard = (): void => {
    if (backGuardEnabled || !window.matchMedia("(pointer: coarse)").matches) return;
    backGuardEnabled = true;
    history.replaceState({ ...history.state, weirdoAppEntry: true }, "");
    pushBackGuard();
    window.addEventListener("popstate", handleBack);
  };

  return {
    bindGestures,
    bindViewportResilience,
    enableBackGuard,
    openSidebar,
    closeSidebar,
    rememberSidebar,
    restoreSidebar(sidebar, preferredScroll) { sidebar.scrollTop = preferredScroll || sidebarScroll; sidebarScroll = sidebar.scrollTop; setSidebarModal(document.body.classList.contains("nav-open")); },
    hapticFeedback,
    hasSelectedText,
    gestureStartsOnControl,
  };
}
